import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';

type State = 'in_stock' | 'low_stock' | 'made_to_order' | 'unavailable';

const DOT: Record<State, string> = {
  in_stock: 'var(--color-success)',
  low_stock: 'var(--color-accent)',
  made_to_order: 'var(--color-muted)',
  unavailable: 'var(--color-danger)',
};

/**
 * An Adams product has no stock figure at all, so it renders as `made_to_order` with a
 * stated lead time. It is never shown as "in stock" — that would be a lie the shop tells
 * on every one of 2031 variants.
 */
export function Availability({
  state,
  quantity,
  leadTimeDays,
  compact = false,
}: {
  state: State;
  quantity: number | null;
  leadTimeDays: [number, number] | null;
  compact?: boolean;
}): JSX.Element {
  const { t } = useTranslation();

  const detail =
    state === 'low_stock' && quantity !== null
      ? t('availability.lowStockCount', { count: quantity })
      : state === 'in_stock' && quantity !== null && !compact
        ? t('availability.inStockCount', { count: quantity })
        : state === 'made_to_order' && leadTimeDays
          ? t('availability.madeToOrderLead', { min: leadTimeDays[0], max: leadTimeDays[1] })
          : null;

  return (
    <span className="inline-flex items-center gap-2 text-xs" data-availability={state}>
      <span
        className="inline-block h-1.5 w-1.5 rounded-full shrink-0"
        style={{ background: DOT[state] }}
        aria-hidden="true"
      />
      <span className="font-semibold">{t(`availability.${state}`)}</span>
      {detail && !compact && <span className="text-[var(--color-muted)]">· {detail}</span>}
    </span>
  );
}
